import Highlight from '@tiptap/extension-highlight';
import { FontSize, TextStyle } from '@tiptap/extension-text-style';
import Typography from '@tiptap/extension-typography';
import { CharacterCount, Placeholder, TrailingNode } from '@tiptap/extensions';
import { Editor, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { EDITOR_PROSE_CLASSNAMES } from './constants';
import { cleanPastedHTML } from './helpers';

type UseTiptapEditorParams = {
    value: string;
    onChange: (value: string) => void;
    disabled: boolean;
    placeholder?: string;
    autoFocus?: boolean;
    editorClassName?: string;
    onEscape?: () => void;
};

export const useTiptapEditor = ({
    value,
    onChange,
    disabled,
    placeholder,
    autoFocus,
    editorClassName,
    onEscape,
}: UseTiptapEditorParams): Editor | null => {
    // Read through a ref: the editor is created once, so a callback captured in
    // `editorProps` would otherwise freeze at its first-render identity.
    const onEscapeRef = useRef(onEscape);
    onEscapeRef.current = onEscape;

    const editor = useEditor({
        extensions: [
            // Links are not part of a laudo, and they don't survive the DOCX
            // export. `link: false` also turns off autolink, which would keep
            // creating them from typed URLs.
            StarterKit.configure({ link: false }),
            Placeholder.configure({ placeholder: placeholder ?? '' }),
            CharacterCount,
            TrailingNode,
            Typography,
            Highlight,
            // Font size replaces heading levels as the way to size text. `heading`
            // stays registered (StarterKit) even though nothing authors one, so
            // reports written before this still round-trip their h1/h2 untouched.
            TextStyle,
            FontSize,
        ],
        content: value,
        autofocus: autoFocus ? 'end' : false,
        editable: !disabled,
        onUpdate: ({ editor: currentEditor }) => {
            onChange(currentEditor.getHTML());
        },
        editorProps: {
            attributes: {
                class: cn(EDITOR_PROSE_CLASSNAMES, editorClassName),
                lang: 'pt-BR',
                spellcheck: 'false',
            },
            transformPastedHTML: cleanPastedHTML,
            // ProseMirror's own key capture preventDefaults Escape whenever the
            // editor has focus, which hides the press from document listeners
            // upstream. Handle it here, at the only layer that still sees it.
            handleKeyDown: (_view, event) => {
                if (event.key !== 'Escape' || !onEscapeRef.current) return false;

                onEscapeRef.current();
                return true;
            },
        },
    });

    useEffect(() => {
        if (!editor || value === editor.getHTML() || editor.isFocused) return;
        editor.commands.setContent(value);
    }, [editor, value]);

    useEffect(() => {
        if (!editor) return;
        editor.setEditable(!disabled);
    }, [disabled, editor]);

    return editor;
};
